import React from "react";
import {
  Box,
  Stack,
  Text,
  Switch,
  Tag,
  Input,
  Checkbox,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useScheduleCheck } from "./useScheduleCheck";

// Default schedule state
export const defaultSchedule = {
  enabled: false,
  schedule: "09:00 - 17:00, 5 days",
  start: "09:00",
  end: "17:00",
  days: {
    Mon: true,
    Tue: true,
    Wed: true,
    Thu: true,
    Fri: true,
    Sat: false,
    Sun: false,
  },
};

const CallTransferSchedule = ({ value, onChange }) => {
  const params = useParams();
  const agentId = params.id;
  const [state, setState] = React.useState(value || defaultSchedule);
  const { scheduleStatus, checkSchedule } = useScheduleCheck(agentId);

  React.useEffect(() => {
    if (value) setState(value);
  }, [value]);

  React.useEffect(() => {
    if (agentId && state) {
      checkSchedule(state);
    }
    // Only run when state or agentId changes
  }, [state, agentId, checkSchedule]);

  // Helper to update state and notify parent
  const update = (updates) => {
    const newState = { ...state, ...updates };
    setState(newState);
    if (onChange) onChange(newState);
  };

  const handleDayToggle = (day) => {
    const newDays = { ...state.days, [day]: !state.days[day] };
    update({ days: newDays });
  };

  // Calculate number of selected days
  const selectedDays = Object.values(state.days).filter(Boolean).length;
  return (
    <Box borderRadius="md" p={4} mb={4}>
      <Stack direction="row" align="center" mb={2}>
        <Text fontWeight="semibold" fontSize="md">
          <span role="img" aria-label="clock">
            🕒
          </span>{" "}
          Time Restrictions
        </Text>
        <Box flex="1" />
        <Switch
          isChecked={state.enabled}
          onChange={() => update({ enabled: !state.enabled })}
        />
      </Stack>
      {/* Only show the rest if enabled */}
      {state.enabled && (
        <>
          {/* Show schedule status only if enabled and we have a response */}
          {scheduleStatus && (
            <Tag
              colorScheme={scheduleStatus.outside ? "red" : "green"}
              mb={2}
              w="fit-content"
              as="span"
            >
              <Box as="span" position="relative">
                {scheduleStatus.outside ? "Outside schedule" : "Inside schedule"}
                {scheduleStatus.current_user_time && (
                  <Box
                    as="span"
                    ml={2}
                    fontSize="sm"
                    color="gray.500"
                    position="relative"
                  >
                    <Box
                      as="span"
                      tabIndex={0}
                      style={{
                        textDecoration: "underline dotted",
                        cursor: "pointer",
                      }}
                      title={`Your time: ${scheduleStatus.current_user_time} (${scheduleStatus.current_user_timezone || ""})`}
                    >
                      ⓘ
                    </Box>
                  </Box>
                )}
              </Box>
            </Tag>
          )}
          {/* Schedule summary text */}
          <Text fontWeight="medium" mb={2}>
            {state.start} - {state.end}, on{" "}
            {selectedDays} day{selectedDays !== 1 ? "s" : ""}
          </Text>
          <Stack direction="row" spacing={2} mb={2}>
            <Box>
              <Text fontSize="sm" mb={1}>
                Start
              </Text>
              <Input
                type="time"
                value={state.start}
                onChange={(e) => update({ start: e.target.value })}
                size="sm"
              />
            </Box>
            <Box>
              <Text fontSize="sm" mb={1}>
                End
              </Text>
              <Input
                type="time"
                value={state.end}
                onChange={(e) => update({ end: e.target.value })}
                size="sm"
              />
            </Box>
          </Stack>
          <Box mb={2}>
            <Text fontSize="sm" mb={1}>
              Days
            </Text>
            <Wrap spacing={2}>
              {Object.keys(state.days).map((day) => (
                <WrapItem key={day}>
                  <Checkbox
                    isChecked={state.days[day]}
                    onChange={() => handleDayToggle(day)}
                    size="sm"
                  >
                    {day}
                  </Checkbox>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        </>
      )}
      {/* Timezone dropdown removed */}
    </Box>
  );
};

export default CallTransferSchedule;